import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { PageHeader, Pill, Btn } from "@/components/kit";
import { usd } from "@/lib/format";
import { CloudLightning, Download, Users, Loader2, RefreshCw, Flame, Search, MapPin, FileText, Bell, X, Save, Layers, ShieldAlert, UserPlus } from "lucide-react";
import { toast } from "sonner";
import HailMap from "@/components/HailMap";
import StormCampaigns from "@/components/StormCampaigns";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DAYS_OPTS = [[30, "30 days"], [90, "90 days"], [180, "6 months"], [365, "1 year"], [1095, "3 years"]];
const MAG_OPTS = [[0.75, "0.75\" +"], [1.0, "1\" +"], [1.5, "1.5\" +"], [2.0, "2\" +"]];
const legend = [["≥ 2\"", "#7f1d1d"], ["1.75–2\"", "#C8102E"], ["1.25–1.75\"", "#D97706"], ["1–1.25\"", "#eab308"], ["< 1\"", "#22c55e"]];

const pointInPoly = (lat, lon, poly) => {
  if (!poly || poly.length < 3) return true;
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const yi = poly[i][0], xi = poly[i][1], yj = poly[j][0], xj = poly[j][1];
    if (((xi > lon) !== (xj > lon)) && (lat < ((yj - yi) * (lon - xi)) / ((xj - xi) || 1e-12) + yi)) inside = !inside;
  }
  return inside;
};
const mapsRoute = (pts) => "https://www.google.com/maps/dir/" + pts.slice(0, 10).map((p) => `${p.lat},${p.lon}`).join("/");

export default function HailTrace() {
  const [storms, setStorms] = useState([]);
  const [active, setActive] = useState(null);
  const [importing, setImporting] = useState(false);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(365);
  const [stateCode, setStateCode] = useState("TX");
  const [mag, setMag] = useState(1.0);
  const [cityInput, setCityInput] = useState("");
  const [showHeat, setShowHeat] = useState(false);
  const [dateFilter, setDateFilter] = useState("");
  const [compareDate, setCompareDate] = useState("");
  const [ringsOff, setRingsOff] = useState(false);
  const [savingLeads, setSavingLeads] = useState(false);
  const [campaignsKey, setCampaignsKey] = useState(0);
  const [serviceArea, setServiceArea] = useState([]);
  const [pin, setPin] = useState(null);
  const [addr, setAddr] = useState("");
  const [locating, setLocating] = useState(false);
  const [canvass, setCanvass] = useState(null);
  const [alerts, setAlerts] = useState({ enabled: false, emails: [], phones: [], min_size_in: 1.5, state: "TX" });
  const [alertsOpen, setAlertsOpen] = useState(false);

  useEffect(() => { api.get("/hailtrace/storms").then((r) => { setStorms(r.data); setActive(r.data[0] || null); }).catch(() => {}); }, []);
  useEffect(() => { api.get("/hailtrace/config").then((r) => { setServiceArea(r.data.service_area || []); setAlerts(r.data.alerts); }).catch(() => {}); }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/hailtrace/reports?days=${days}&state=${stateCode}&mag=${mag}&city=${encodeURIComponent(cityInput)}`);
      setReports(data.reports || []);
      setDateFilter("");
      setCompareDate("");
    } catch { toast.error("Could not load storm reports"); setReports([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadReports(); /* eslint-disable-next-line */ }, [days, stateCode, mag]);

  const buildCanvass = (points) => {
    const byCity = {};
    points.forEach((r) => { const k = `${r.city || "Unknown"} · ${r.county} Co`; (byCity[k] ||= { key: k, count: 0, max: 0, pts: [] }); byCity[k].count++; byCity[k].max = Math.max(byCity[k].max, r.size_in); byCity[k].pts.push(r); });
    setCanvass({ total: points.length, groups: Object.values(byCity).sort((a, b) => b.count - a.count), pts: points });
  };

  const saveServiceArea = async (coords) => {
    try { await api.put("/hailtrace/config", { service_area: coords || [] }); setServiceArea(coords || []); toast.success(coords ? "Area saved — review & import the properties inside" : "Service area cleared"); }
    catch { toast.error("Could not save the area"); }
    if (coords && coords.length >= 3) {
      const base = dateFilter ? reports.filter((r) => (r.valid || "").startsWith(dateFilter)) : reports;
      buildCanvass(base.filter((r) => pointInPoly(r.lat, r.lon, coords)));
    } else { setCanvass(null); }
  };

  const onCanvassBox = (bounds) => {
    if (!bounds) { setCanvass(null); return; }
    const src = dateFilter ? reports.filter((r) => (r.valid || "").startsWith(dateFilter)) : reports;
    buildCanvass(src.filter((r) => r.lat >= bounds.south && r.lat <= bounds.north && r.lon >= bounds.west && r.lon <= bounds.east));
  };

  const locateAddress = async () => {
    if (!addr.trim()) return;
    setLocating(true);
    try { const { data } = await api.get(`/geocode?q=${encodeURIComponent(addr)}`); setPin({ lat: data.lat, lon: data.lon, label: addr }); }
    catch { toast.error("Address not found"); } finally { setLocating(false); }
  };

  const downloadReport = async () => {
    const target = pin?.label || addr;
    if (!target) return toast.error("Enter a property address first");
    toast.info("Building hail report…");
    try {
      const { data } = await api.get(`/hailtrace/report.pdf?address=${encodeURIComponent(target)}&days=${days}&state=${stateCode}&mag=${mag}&radius=15`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Could not build the report"); }
  };

  const saveAlerts = async () => {
    try { const { data } = await api.put("/hailtrace/config", { alerts: { ...alerts, emails: alerts.emails, phones: alerts.phones } }); setAlerts(data.alerts); setAlertsOpen(false); toast.success("Alert settings saved"); }
    catch { toast.error("Only owners can change alerts"); }
  };

  const saveStormLeads = async () => {
    if (!canvass?.pts?.length) return toast.error("Draw a box or circle an area over the markers first");
    // Use the selected storm day, else derive it from the most recent report inside the selection.
    let stormDate = dateFilter;
    if (!stormDate) {
      const dates = canvass.pts.map((r) => (r.valid || "").slice(0, 10)).filter(Boolean).sort();
      stormDate = dates[dates.length - 1] || new Date().toISOString().slice(0, 10);
    }
    setSavingLeads(true);
    try {
      const { data } = await api.post("/hailtrace/save-leads", { storm_date: stormDate, points: canvass.pts });
      toast.success(`${data.created} leads imported (tag ${data.tag})${data.skipped ? `, ${data.skipped} already existed` : ""}`);
      setCampaignsKey((k) => k + 1);
    } catch { toast.error("Could not import leads"); } finally { setSavingLeads(false); }
  };

  const importLeads = async () => {
    if (!active) return toast.error("No storm event selected");
    setImporting(true);
    try { const { data } = await api.post("/hailtrace/import", { storm_id: active.id, count: 12 }); toast.success(`${data.imported} scored leads imported → CRM (tag ${data.tag})`); }
    catch { toast.error("Import failed"); } finally { setImporting(false); }
  };

  const exportCsv = () => {
    const src = dateFilter ? reports.filter((r) => (r.valid || "").startsWith(dateFilter)) : reports;
    if (!src.length) return toast.error("No reports to export");
    const rows = [["Size(in)", "City", "County", "State", "Lat", "Lon", "Time(UTC)", "Source"]];
    src.forEach((r) => rows.push([r.size_in, r.city, r.county, r.state, r.lat, r.lon, r.valid, r.source]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `hail_reports_${stateCode}_${dateFilter || days + "d"}.csv`; a.click();
    URL.revokeObjectURL(url); toast.success(`Exported ${src.length} hail reports`);
  };

  const stormDays = useMemo(() => {
    const m = {};
    reports.forEach((r) => { const d = (r.valid || "").slice(0, 10); if (!d) return; (m[d] ||= { date: d, count: 0, max: 0 }); m[d].count++; m[d].max = Math.max(m[d].max, r.size_in); });
    return Object.values(m).sort((a, b) => b.count - a.count).slice(0, 20).sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [reports]);
  const shown = dateFilter ? reports.filter((r) => (r.valid || "").startsWith(dateFilter)) : reports;
  const fmtDay = (d) => new Date(d + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  const cell = (r) => `${Math.round(r.lat / 0.08)}_${Math.round(r.lon / 0.08)}`;
  const compareOn = !!dateFilter && !!compareDate && compareDate !== dateFilter;
  let mapReports = shown, colorMode = "size", twiceCount = 0, dayA = 0, dayB = 0;
  if (compareOn) {
    const A = reports.filter((r) => (r.valid || "").startsWith(dateFilter));
    const B = reports.filter((r) => (r.valid || "").startsWith(compareDate));
    dayA = A.length; dayB = B.length;
    const cellsA = new Set(A.map(cell)), cellsB = new Set(B.map(cell));
    const both = new Set([...cellsA].filter((c) => cellsB.has(c)));
    twiceCount = both.size;
    mapReports = [...A.map((r) => ({ ...r, grp: both.has(cell(r)) ? "both" : "A" })), ...B.map((r) => ({ ...r, grp: both.has(cell(r)) ? "both" : "B" }))];
    colorMode = "group";
  }
  const showRings = !!dateFilter && !compareOn && !ringsOff;
  const maxHail = shown.reduce((m, r) => Math.max(m, r.size_in), 0);
  const insideCount = serviceArea.length >= 3 ? shown.filter((r) => pointInPoly(r.lat, r.lon, serviceArea)).length : null;
  const roi = active?.roi;
  const setArr = (k, v) => setAlerts((a) => ({ ...a, [k]: v.split(",").map((x) => x.trim()).filter(Boolean) }));

  return (
    <div>
      <PageHeader title="Hail Trace Lead Generator" subtitle="Live hail storm map · draw your service area · canvass · insurance-ready reports" />
      <div className="rounded-xl bg-amberw/10 border-2 border-amberw/30 p-3 mb-4 flex items-start gap-2">
        <CloudLightning className="w-4 h-4 shrink-0 text-amberw mt-0.5" />
        <p className="text-sm text-amberw">Draw a <b>rectangle</b> or <b>polygon</b> around the homes you want, then <b>import</b> them as leads. Lead scoring &amp; ROI stay in demo mode until parcels are connected.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 mb-card p-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h3 className="font-display font-bold uppercase text-lg text-navy">Hail Storm Map</h3>
            <div className="flex flex-wrap items-center gap-2">
              <input data-testid="hail-state" value={stateCode} onChange={(e) => setStateCode(e.target.value.toUpperCase().slice(0, 2))} maxLength={2}
                className="w-14 border-2 border-slate-200 rounded-lg px-2 py-1.5 text-sm font-num uppercase outline-none focus:border-navy" placeholder="TX" />
              <input data-testid="hail-city" value={cityInput} onChange={(e) => setCityInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadReports()}
                className="w-28 border-2 border-slate-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-navy" placeholder="City/County" />
              <select data-testid="hail-days" value={days} onChange={(e) => setDays(Number(e.target.value))} className="border-2 border-slate-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-navy">
                {DAYS_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <select data-testid="hail-mag" value={mag} onChange={(e) => setMag(Number(e.target.value))} className="border-2 border-slate-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-navy">
                {MAG_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <select data-testid="hail-storm-date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} title="Jump to one storm day" className="border-2 border-slate-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-navy max-w-[210px]">
                <option value="">All storm days</option>
                {stormDays.map((s) => <option key={s.date} value={s.date}>{fmtDay(s.date)} · {s.count} · {s.max.toFixed(2)}"</option>)}
              </select>
              {dateFilter && (
                <select data-testid="hail-storm-date-2" value={compareDate} onChange={(e) => setCompareDate(e.target.value)} title="Compare with a second storm day" className="border-2 border-brandred/40 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-brandred max-w-[210px]">
                  <option value="">+ Compare day…</option>
                  {stormDays.filter((s) => s.date !== dateFilter).map((s) => <option key={s.date} value={s.date}>vs {fmtDay(s.date)} · {s.count}</option>)}
                </select>
              )}
              {dateFilter && !compareOn && (
                <button type="button" data-testid="hail-severity-toggle" onClick={() => setRingsOff((v) => !v)} title="Shade ≥2&quot; damage zones"
                  className={`p-1.5 rounded-lg border-2 ${!ringsOff ? "bg-brandred/10 text-brandred border-brandred/40" : "border-slate-200 text-slate-400"}`}><ShieldAlert className="w-4 h-4" /></button>
              )}
              <button type="button" data-testid="hail-heat-toggle" onClick={() => setShowHeat((v) => !v)} title="Toggle heat swath"
                className={`p-1.5 rounded-lg border-2 ${showHeat ? "bg-brandred text-white border-brandred" : "border-slate-200 text-slate-500"}`}><Flame className="w-4 h-4" /></button>
              <button type="button" data-testid="hail-refresh" onClick={loadReports} className="text-slate-500 hover:text-navy p-1.5" title="Refresh"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="flex-1 min-w-[220px] flex items-center gap-2 border-2 border-slate-200 rounded-lg px-2 py-1.5">
              <MapPin className="w-4 h-4 text-brandred shrink-0" />
              <input data-testid="hail-address" value={addr} onChange={(e) => setAddr(e.target.value)} onKeyDown={(e) => e.key === "Enter" && locateAddress()}
                className="flex-1 text-sm outline-none" placeholder="Find a property address on the map…" />
              <button type="button" data-testid="hail-locate" onClick={locateAddress} className="text-navy" title="Locate">{locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}</button>
            </div>
            <Btn variant="ghost" onClick={downloadReport} testid="hail-report-btn"><FileText className="w-4 h-4" /> Insurance Report</Btn>
          </div>

          <div className="relative">
            {compareOn && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2 text-xs" data-testid="compare-banner">
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: "#2563eb" }} /> {fmtDay(dateFilter)} ({dayA})</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: "#f97316" }} /> {fmtDay(compareDate)} ({dayB})</span>
                <span className="inline-flex items-center gap-1 font-semibold text-navy"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: "#7c3aed" }} /> Hit twice: {twiceCount} zone(s)</span>
                <button type="button" onClick={() => setCompareDate("")} className="text-slate-500 hover:text-brandred underline font-semibold">clear compare</button>
              </div>
            )}
            <HailMap reports={mapReports} showHeat={showHeat} serviceArea={serviceArea} pin={pin} colorMode={colorMode} severityRings={showRings} onServiceArea={saveServiceArea} onCanvassBox={onCanvassBox} />
            {loading && <div className="absolute inset-0 bg-white/60 flex items-center justify-center rounded-xl z-[400]"><Loader2 className="w-6 h-6 animate-spin text-navy" /></div>}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-slate-500" data-testid="hail-legend">
            <span className="font-semibold text-slate-600">Hail size:</span>
            {legend.map(([l, c]) => (<span key={l} className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: c }} /> {l}</span>))}
          </div>

          <div className="grid grid-cols-4 gap-3 mt-4 text-center font-num">
            <div><div className="text-2xl font-bold text-navy" data-testid="hail-count">{shown.length.toLocaleString()}</div><div className="text-[10px] uppercase text-slate-400">Hail Reports</div></div>
            <div><div className="text-2xl font-bold text-brandred">{maxHail ? maxHail.toFixed(2) : "—"}"</div><div className="text-[10px] uppercase text-slate-400">Max Hail</div></div>
            <div><div className="text-2xl font-bold text-navy">{shown.filter((r) => r.size_in >= 2).length}</div><div className="text-[10px] uppercase text-slate-400">Reports ≥ 2"</div></div>
            <div><div className="text-2xl font-bold text-successg" data-testid="hail-inside">{insideCount ?? "—"}</div><div className="text-[10px] uppercase text-slate-400">In Service Area</div></div>
          </div>
          <div className="flex gap-2 mt-4">
            <Btn variant="red" onClick={importLeads} disabled={importing} testid="import-leads-btn" className="flex-1">{importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />} Bulk Import to CRM</Btn>
            <Btn variant="ghost" onClick={exportCsv} testid="export-csv-btn"><Download className="w-4 h-4" /> CSV</Btn>
            {serviceArea.length >= 3 && <Btn variant="ghost" onClick={() => saveServiceArea(null)} testid="clear-area-btn"><X className="w-4 h-4" /> Area</Btn>}
          </div>
        </div>

        <div className="space-y-4">
          {canvass && (
            <div className="mb-card p-5" data-testid="canvass-panel">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-display font-bold uppercase text-navy">Selected Area</h3>
                <button onClick={() => setCanvass(null)} className="text-slate-400 hover:text-brandred"><X className="w-4 h-4" /></button>
              </div>
              <p className="text-xs text-slate-500 mb-2">{canvass.total} hail report(s) in the selected area</p>
              {canvass.total > 0 ? (
                <>
                  <div className="space-y-1.5 max-h-52 overflow-y-auto">
                    {canvass.groups.map((g) => (
                      <div key={g.key} className="flex justify-between text-sm border-b border-slate-100 py-1" data-testid={`canvass-row-${g.key}`}>
                        <span className="text-navy">{g.key}</span><span className="font-num text-slate-500">{g.count} · {g.max.toFixed(2)}"</span>
                      </div>
                    ))}
                  </div>
                  <a href={mapsRoute(canvass.pts)} target="_blank" rel="noreferrer" data-testid="canvass-route" className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-navy text-white text-sm font-bold py-2 rounded-xl">
                    <MapPin className="w-4 h-4" /> Open door-knock route
                  </a>
                  <button type="button" data-testid="canvass-save-leads" onClick={saveStormLeads} disabled={savingLeads}
                    className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-brandred text-white text-sm font-bold py-2 rounded-xl border-2 border-brandred disabled:opacity-60">
                    {savingLeads ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Import {canvass.total} propert{canvass.total === 1 ? "y" : "ies"} as leads
                  </button>
                  <p className="text-[11px] text-slate-400 mt-1">{dateFilter ? "Tagged to the selected storm day." : "Auto-tagged by the latest storm date in your selection."}</p>
                </>
              ) : <p className="text-sm text-slate-400">No reports in that box — draw over the markers.</p>}
            </div>
          )}

          <div className="mb-card p-5">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-display font-bold uppercase text-navy">Storm Alerts</h3>
              <button data-testid="alerts-config-btn" onClick={() => setAlertsOpen(true)} className="text-navy hover:text-brandred" title="Configure"><Bell className="w-4 h-4" /></button>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Pill tone={alerts.enabled ? "green" : "amber"}>{alerts.enabled ? "On" : "Off"}</Pill>
              <span className="text-slate-500">Daily email ≥ {alerts.min_size_in}" · {alerts.emails?.length || 0} recipient(s)</span>
            </div>
          </div>

          <div className="mb-card p-5">
            <h3 className="font-display font-bold uppercase text-navy mb-3">Storm Events</h3>
            {storms.length ? storms.map((s) => (
              <button key={s.id} data-testid={`storm-${s.id}`} onClick={() => setActive(s)}
                className={`w-full text-left p-3 rounded-xl mb-2 border-2 ${active?.id === s.id ? "border-navy bg-white" : "border-slate-200"}`}>
                <div className="flex justify-between items-start"><span className="font-semibold text-navy text-sm">{s.name}</span><Pill tone={s.severity === "severe" ? "danger" : "amber"}>{s.severity}</Pill></div>
                <div className="text-xs text-slate-400 mt-1">{s.date}</div>
              </button>
            )) : <p className="text-sm text-slate-400">No storm events</p>}
          </div>
          {roi && (
            <div className="mb-card p-5">
              <h3 className="font-display font-bold uppercase text-navy mb-3">Storm ROI</h3>
              {[["Leads", roi.leads], ["Appointments", roi.appointments], ["Proposals", roi.proposals], ["Signed", roi.signed]].map(([l, v]) => (
                <div key={l} className="flex justify-between py-1.5 border-b border-slate-100 text-sm"><span className="text-slate-500">{l}</span><span className="font-num font-bold text-navy">{v}</span></div>
              ))}
              <div className="flex justify-between pt-2 text-sm"><span className="text-slate-500">Revenue</span><span className="font-num font-bold text-successg">{usd(roi.revenue_cents)}</span></div>
            </div>
          )}
        </div>
      </div>

      <StormCampaigns refreshKey={campaignsKey} />

      {alertsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setAlertsOpen(false)}>
          <div className="mb-card p-6 w-full max-w-md animate-scale-in" onClick={(e) => e.stopPropagation()} data-testid="alerts-modal">
            <div className="flex justify-between items-center mb-4"><h3 className="font-display text-2xl font-bold uppercase text-navy">Storm Auto-Alerts</h3><button onClick={() => setAlertsOpen(false)}><X className="w-5 h-5 text-slate-400" /></button></div>
            <label className="flex items-center gap-2 text-sm font-semibold text-navy mb-3"><input data-testid="alert-enabled" type="checkbox" checked={alerts.enabled} onChange={(e) => setAlerts((a) => ({ ...a, enabled: e.target.checked }))} /> Email me daily when new large hail lands</label>
            <div className="space-y-3">
              <div><label className="text-xs font-semibold uppercase text-slate-500">Email recipients (comma-separated)</label>
                <input data-testid="alert-emails" defaultValue={(alerts.emails || []).join(", ")} onChange={(e) => setArr("emails", e.target.value)} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy" placeholder="you@company.com, owner@company.com" /></div>
              <div><label className="text-xs font-semibold uppercase text-slate-500">Text recipients (phones, optional)</label>
                <input data-testid="alert-phones" defaultValue={(alerts.phones || []).join(", ")} onChange={(e) => setArr("phones", e.target.value)} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy" placeholder="(210) 555-1234" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-semibold uppercase text-slate-500">Min hail size</label>
                  <select data-testid="alert-mag" value={alerts.min_size_in} onChange={(e) => setAlerts((a) => ({ ...a, min_size_in: Number(e.target.value) }))} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-navy">
                    {[0.75, 1.0, 1.25, 1.5, 1.75, 2.0].map((v) => <option key={v} value={v}>{v}"</option>)}</select></div>
                <div><label className="text-xs font-semibold uppercase text-slate-500">State</label>
                  <input data-testid="alert-state" value={alerts.state} maxLength={2} onChange={(e) => setAlerts((a) => ({ ...a, state: e.target.value.toUpperCase().slice(0, 2) }))} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm font-num uppercase outline-none focus:border-navy" /></div>
              </div>
              <p className="text-xs text-slate-400">Only reports inside your drawn service area are sent (draw a polygon on the map). Text delivery requires Twilio setup; email is active now.</p>
            </div>
            <Btn onClick={saveAlerts} className="w-full mt-4" testid="save-alerts"><Save className="w-4 h-4" /> Save Alerts</Btn>
          </div>
        </div>
      )}
    </div>
  );
}
